import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { DraggableTable } from './DraggableTable'
import { getShape } from './TableShapeRenderer'
import type { TableCombination, TableState } from '@/lib/types/table'

const PADDING = 12
const HEADER_H = 28

interface CombinationContainerProps {
  combo: TableCombination
  areaId: string
  tableStates: Map<number, { state: TableState; guestName?: string }>
  onBreakCombo?: (documentId: string) => void
  posOrdersByTableDocumentId?: Map<string, 'none' | 'open' | 'in_progress' | 'paid'>
  onPOSTableClick?: (tableDocumentId: string) => void
}

// Most significant booking state across a set of table states
function dominantState(states: { state: TableState; guestName?: string }[]): { state: TableState; guestName?: string } {
  const priority: TableState[] = ['occupied', 'reserved', 'waitlist', 'available', 'not_bookable']
  for (const s of priority) {
    const match = states.find((st) => st.state === s)
    if (match) return match
  }
  return states[0] ?? { state: 'available' }
}

// POS status color helpers
const POS_STATUS_COLORS: Record<string, { dot: string; label: string; text: string }> = {
  open:        { dot: 'bg-green-400',  label: 'Open',        text: 'text-green-400' },
  in_progress: { dot: 'bg-amber-400',  label: 'In progress', text: 'text-amber-400' },
  paid:        { dot: 'bg-blue-400',   label: 'Paid',        text: 'text-blue-400' },
  none:        { dot: 'bg-zinc-600',   label: '',            text: 'text-zinc-500' },
}

// Booking state → overlay accent
const STATE_COLORS: Record<TableState, { border: string; bg: string; dot: string }> = {
  occupied:     { border: 'rgba(251,191,36,0.55)',  bg: 'rgba(251,191,36,0.08)',  dot: 'bg-amber-400' },
  reserved:     { border: 'rgba(251,191,36,0.35)',  bg: 'rgba(251,191,36,0.05)',  dot: 'bg-amber-300' },
  waitlist:     { border: 'rgba(251,146,60,0.45)',  bg: 'rgba(251,146,60,0.06)',  dot: 'bg-orange-400' },
  available:    { border: 'rgba(74,222,128,0.55)',  bg: 'rgba(74,222,128,0.06)',  dot: 'bg-green-400' },
  not_bookable: { border: 'rgba(113,113,122,0.35)', bg: 'rgba(113,113,122,0.04)', dot: 'bg-zinc-600' },
}

export function CombinationContainer({
  combo,
  areaId,
  tableStates,
  onBreakCombo,
  posOrdersByTableDocumentId,
  onPOSTableClick,
}: CombinationContainerProps) {
  const { positions, mode } = useFloorPlanStore()
  const comboTables = combo.tables ?? []

  // Compute bounding box of all tables in this combo
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const t of comboTables) {
    const pos = positions[String(t.id)]
    if (!pos) continue
    const { width, height } = getShape(t.seatsMax, t.shape)
    if (pos.x < minX) minX = pos.x
    if (pos.y < minY) minY = pos.y
    if (pos.x + width > maxX) maxX = pos.x + width
    if (pos.y + height > maxY) maxY = pos.y + height
  }

  const containerW = maxX - minX + PADDING * 2
  const containerH = maxY - minY + PADDING * 2 + HEADER_H

  const { setNodeRef, listeners, attributes, transform, isDragging } = useDraggable({
    id: `combo-${combo.documentId}`,
    data: { areaId, tableIds: comboTables.map((t) => t.id) },
    disabled: mode === 'live' || mode === 'pos',
  })

  if (!isFinite(minX)) return null

  const isLiveOrPOS = mode === 'live' || mode === 'pos'

  // Compute dominant POS status across all tables in combo
  const posStatus = (() => {
    if (!posOrdersByTableDocumentId) return 'none'
    const statuses = comboTables.map((t) => posOrdersByTableDocumentId.get(t.documentId) ?? 'none')
    if (statuses.includes('open')) return 'open'
    if (statuses.includes('in_progress')) return 'in_progress'
    if (statuses.includes('paid')) return 'paid'
    return 'none'
  })()

  // Dominant booking state
  const bookingStates = comboTables.map((t) => tableStates.get(t.id) ?? { state: 'available' as TableState })
  const dom = dominantState(bookingStates)

  // Layout mode: improve visuals but keep individual tables editable
  if (mode === 'layout') {
    const blockedByTable = posOrdersByTableDocumentId
      ? comboTables.find((t) => {
          const s = posOrdersByTableDocumentId.get(t.documentId)
          return s === 'open' || s === 'in_progress'
        })
      : undefined
    const blocked = !!blockedByTable

    return (
      <div
        ref={setNodeRef}
        data-table-node
        style={{
          position: 'absolute',
          left: minX - PADDING,
          top: minY - PADDING - HEADER_H,
          width: containerW,
          height: containerH,
          transform: CSS.Translate.toString(transform),
          zIndex: isDragging ? 50 : 2,
          opacity: isDragging ? 0.85 : 1,
          cursor: 'grab',
        }}
        {...listeners}
        {...attributes}
      >
        {/* Frame */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '1.5px solid rgba(74,222,128,0.55)',
            borderRadius: 12,
            backgroundColor: 'rgba(74,222,128,0.06)',
            pointerEvents: 'none',
          }}
        />

        {/* Header row */}
        <div
          style={{
            position: 'absolute',
            top: 5,
            left: PADDING,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: 'rgba(255,255,255,0.75)',
              userSelect: 'none',
            }}
          >
            {combo.name} · {combo.seatsMax}p
          </span>

          {/* Break button — stops pointer propagation so it doesn't start a drag */}
          {onBreakCombo && (
            <button
              style={{
                pointerEvents: 'all',
                width: 20,
                height: 20,
                borderRadius: 5,
                backgroundColor: blocked ? 'rgba(161,161,170,0.10)' : 'rgba(239,68,68,0.15)',
                border: `1px solid ${blocked ? 'rgba(161,161,170,0.25)' : 'rgba(239,68,68,0.40)'}`,
                color: blocked ? 'rgba(161,161,170,0.4)' : 'rgba(239,68,68,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                lineHeight: 1,
                cursor: blocked ? 'not-allowed' : 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation()
                if (blocked) return
                onBreakCombo(combo.documentId)
              }}
              onPointerDown={(e) => e.stopPropagation()}
              title={blocked
                ? `Cannot uncombine — ${blockedByTable!.name} has an active order`
                : 'Break combination'}
            >
              ×
            </button>
          )}
        </div>

        {/* Individual tables at relative positions */}
        {comboTables.map((t) => {
          const pos = positions[String(t.id)]
          if (!pos) return null
          const { state, guestName } = tableStates.get(t.id) ?? { state: 'available' as const }
          return (
            <DraggableTable
              key={t.id}
              table={t}
              state={state}
              guestName={guestName}
              areaId={areaId}
              overridePos={{ x: pos.x - minX + PADDING, y: pos.y - minY + PADDING + HEADER_H }}
            />
          )
        })}
      </div>
    )
  }

  // Live / POS mode: same frame+header structure as layout mode, state-colored border
  const sc = STATE_COLORS[dom.state] ?? STATE_COLORS.available
  const pc = POS_STATUS_COLORS[posStatus] ?? POS_STATUS_COLORS.none

  return (
    <div
      data-table-node
      style={{
        position: 'absolute',
        left: minX - PADDING,
        top: minY - PADDING - HEADER_H,
        width: containerW,
        height: containerH,
        zIndex: 3,
      }}
    >
      {/* State-colored frame */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          border: `1.5px solid ${sc.border}`,
          borderRadius: 12,
          backgroundColor: sc.bg,
          pointerEvents: 'none',
        }}
      />

      {/* Header row */}
      <div
        style={{
          position: 'absolute',
          top: 5,
          left: PADDING,
          right: PADDING,
          height: HEADER_H - 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {/* Combo name + seats */}
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: 'rgba(255,255,255,0.75)',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {combo.name} · {combo.seatsMax}p
        </span>

        {/* Right side: guest name + POS dot */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          {dom.guestName && (
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'rgba(251,191,36,0.85)',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}>
              {dom.guestName}
            </span>
          )}
          {posStatus !== 'none' && (
            <span className={`h-1.5 w-1.5 rounded-full ${pc.dot} inline-block`} />
          )}
        </div>
      </div>

      {/* Individual tables — fully interactive */}
      {comboTables.map((t) => {
        const pos = positions[String(t.id)]
        if (!pos) return null
        const { state } = tableStates.get(t.id) ?? { state: 'available' as const }
        return (
          <DraggableTable
            key={t.id}
            table={t}
            state={state}
            areaId={areaId}
            overridePos={{ x: pos.x - minX + PADDING, y: pos.y - minY + PADDING + HEADER_H }}
            posOrderStatus={posOrdersByTableDocumentId?.get(t.documentId)}
            onPOSTableClick={onPOSTableClick}
          />
        )
      })}
    </div>
  )
}
